/**
 * LeadRotationPanel — SLA & Rotation card for the lead detail page.
 *
 * Shows three things, top-to-bottom:
 *   1. Current assignment + live SLA countdown (ticks every second).
 *   2. "Missed by …" badges — one chip per agent that previously had
 *      this lead and missed the SLA window.
 *   3. The rotation timeline — every assignment / miss / attended /
 *      escalation in chronological order.
 *
 * Renders nothing for leads that have no SLA tracking AND no rotation
 * history, so it stays invisible for everyday leads and only appears
 * where it matters.
 *
 * Data source: GET /api/leads/{id}/rotation-history — built in Phase 1
 * of the Lead Rotation feature.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  AlertTriangle, CheckCircle2, Clock, ChevronRight, RefreshCw,
  ShieldAlert, UserMinus, UserPlus,
} from 'lucide-react';
import HelpInfo from './HelpInfo';

const API = process.env.REACT_APP_BACKEND_URL;

// Status → visual treatment for a rotation history row.
const STATUS_STYLES = {
  assigned: { pill: 'bg-blue-100 text-blue-700 border-blue-300', icon: UserPlus, label: 'Assigned' },
  attended: { pill: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2, label: 'Attended' },
  missed: { pill: 'bg-red-100 text-red-700 border-red-300', icon: UserMinus, label: 'Missed' },
  rotated: { pill: 'bg-orange-100 text-orange-700 border-orange-300', icon: RefreshCw, label: 'Rotated' },
  escalated: { pill: 'bg-rose-200 text-rose-800 border-rose-400', icon: ShieldAlert, label: 'Escalated' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// "Due in 2:34" / "Overdue 1m 12s" — the live SLA countdown badge.
const formatCountdown = (ms) => {
  const overdue = ms < 0;
  const total = Math.floor(Math.abs(ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  const stamp = m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
  return { overdue, label: overdue ? `Overdue ${stamp}` : `Due in ${stamp}` };
};

export default function LeadRotationPanel({ contact }) {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [showAll, setShowAll] = useState(false);
  const tickRef = useRef(null);

  const leadId = contact?.id;

  const fetchHistory = useCallback(async () => {
    if (!token || !leadId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/leads/${leadId}/rotation-history`,
        { params: { token } },
      );
      const rows = res.data?.data?.history || [];
      // Earliest first — easier to read top→bottom as a timeline.
      rows.sort((a, b) => (a.rotation_number ?? 0) - (b.rotation_number ?? 0));
      setHistory(rows);
    } catch (e) {
      // Endpoint not yet reachable or network issue — fail quietly,
      // the panel just hides itself.
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [token, leadId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // 1-second tick for the live SLA countdown.
  useEffect(() => {
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  // SLA deadline = assigned_at + sla_breach_minutes (when both set).
  const sla = useMemo(() => {
    const assignedAt = contact?.assigned_at;
    const breachMin = Number(contact?.sla_breach_minutes);
    if (!assignedAt || !breachMin) return null;
    const start = new Date(assignedAt).getTime();
    if (Number.isNaN(start)) return null;
    const deadline = start + breachMin * 60_000;
    return { deadline, remainingMs: deadline - now };
  }, [contact?.assigned_at, contact?.sla_breach_minutes, now]);

  // Derive missed-by badges from the timeline.
  const missedAgents = useMemo(
    () => history
      .filter((h) => h.status === 'missed' && h.assigned_agent_name)
      .map((h) => h.assigned_agent_name),
    [history],
  );

  const isAttended = !!contact?.has_been_responded;
  const isCritical = !!contact?.is_critical_missed;
  const hasSla = !!(contact?.sla_breach_minutes && contact?.assigned_at);
  const hasHistory = history.length > 0;

  // Invisible for everyday leads: only appears when there's something to show.
  if (!hasSla && !hasHistory && !isCritical) return null;

  // Compact / full history toggle.
  const visibleHistory = showAll ? history : history.slice(-4);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Clock size={14} className="text-violet-500" />
          SLA &amp; Rotation
          <HelpInfo
            align="inline"
            title="How SLA & Rotation works"
            steps={[
              <>Every assigned lead has an <strong>SLA window</strong> (default 5 min) within which the assigned agent must log a real action — call, WhatsApp, SMS or email. Status changes and notes don't count.</>,
              <>If the window expires with no action, the lead <strong>auto-rotates</strong> to the next agent in the pool. The previous agent gets a <em>Missed by …</em> badge here.</>,
              <>If all eligible agents miss, the lead is <strong>escalated</strong> to the manager queue — see the red banner.</>,
              <>The timeline below records every assignment, miss and rotation, in order.</>,
            ]}
            tip="The fastest way to clear a missed lead from your name is to log a call or WhatsApp — the SLA timer stops the moment a valid action lands."
          />
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-6 w-6 p-0"
            onClick={fetchHistory}
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Critical banner — short-circuits the rest visually. */}
        {isCritical && (
          <div className="rounded-md border border-rose-300 bg-rose-50 p-3 flex items-start gap-2">
            <ShieldAlert size={14} className="text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-semibold text-rose-700">Critical — all agents missed</p>
              <p className="text-rose-600 mt-0.5">
                {history[history.length - 1]?.rotation_number || 'Multiple'} rotations
                with no contact. In the manager queue for manual reassignment.
              </p>
            </div>
          </div>
        )}

        {/* Current assignment + live SLA countdown. */}
        {hasSla && !isCritical && (
          <div className="rounded-md border border-slate-200 p-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Currently assigned</span>
              {isAttended ? (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-300">
                  <CheckCircle2 size={10} className="mr-0.5" /> Attended
                </Badge>
              ) : sla && (
                (() => {
                  const c = formatCountdown(sla.remainingMs);
                  const cls = c.overdue
                    ? 'bg-red-100 text-red-700 border-red-300'
                    : sla.remainingMs < 60_000
                      ? 'bg-amber-100 text-amber-700 border-amber-300'
                      : 'bg-emerald-100 text-emerald-700 border-emerald-300';
                  return (
                    <Badge className={`text-[10px] border ${cls}`}>
                      <Clock size={10} className="mr-0.5" /> {c.label}
                    </Badge>
                  );
                })()
              )}
            </div>
            <div className="text-sm font-medium text-slate-900">
              {contact.assigned_to_name || contact.owner_name || '— Unassigned'}
            </div>
            <div className="text-[11px] text-slate-500">
              Assigned {fmtTime(contact.assigned_at)} · SLA {contact.sla_breach_minutes}m
            </div>
          </div>
        )}

        {/* Missed-by badges — one chip per prior miss. */}
        {missedAgents.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">
              Previously missed by
            </p>
            <div className="flex flex-wrap gap-1">
              {missedAgents.map((name, i) => (
                <Badge
                  key={`${name}-${i}`}
                  className="text-[10px] bg-red-50 text-red-700 border border-red-200"
                >
                  <UserMinus size={9} className="mr-0.5" /> {name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Timeline. */}
        {loading && !history.length ? (
          <Skeleton className="h-16 w-full" />
        ) : history.length > 0 ? (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">
              Rotation timeline
            </p>
            <ol className="space-y-1.5">
              {visibleHistory.map((h) => {
                const style = STATUS_STYLES[h.status] || STATUS_STYLES.assigned;
                const Icon = style.icon;
                return (
                  <li
                    key={h.id || `${h.rotation_number}-${h.assigned_agent_id}`}
                    className="flex items-start gap-2 text-[11px]"
                  >
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] flex-shrink-0 ${style.pill}`}>
                      <Icon size={9} /> #{h.rotation_number ?? 0}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {h.assigned_agent_name || (h.status === 'escalated' ? '(Manager Queue)' : 'Unknown')}
                      </div>
                      <div className="text-slate-500 leading-tight">
                        {h.status === 'attended' && h.attended_at
                          ? <>Attended {fmtTime(h.attended_at)}{h.attended_by_action ? ` · ${h.attended_by_action}` : ''}</>
                          : h.status === 'missed' && h.missed_at
                            ? <>Missed {fmtTime(h.missed_at)}</>
                            : h.status === 'escalated'
                              ? <>Escalated {fmtTime(h.assigned_at)}</>
                              : <>Assigned {fmtTime(h.assigned_at)}</>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
            {history.length > 4 && (
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="mt-2 text-[11px] text-violet-600 hover:underline inline-flex items-center gap-0.5"
              >
                {showAll ? 'Show recent only' : `Show all (${history.length})`}
                <ChevronRight size={11} className={showAll ? 'rotate-90 transition-transform' : 'transition-transform'} />
              </button>
            )}
          </div>
        ) : hasSla ? (
          <p className="text-[11px] text-slate-400 italic">No rotations yet — first assignment is the current one.</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
